import boardManifest from './demo-board/board.json';
import metadataJson from './demo-board/metadata.json';
import engineJson from './demo-board/engine.json';
import rulesJson from './demo-board/rules.json';
import uiJson from './demo-board/ui.json';
import topologyJson from './demo-board/topology.json';
import dependenciesJson from './demo-board/dependencies.json';
import spaceImage from './demo-board/assets/space.png';
import previewImage from './demo-board/preview.png';
import { EXAMPLE_PLUGIN_VERSION, EXAMPLE_PLUGIN_CDN, EXAMPLE_PLUGIN_REQUIREMENT } from '../version.js';

// Rebuild the monolithic board object from the modular bundle files
// Note: mark as plugin-bundled so the host doesn't try to resolve Node-style deps from a remote build
const pluginRequirement = {
    id: 'example-everything-plugin',
    version: EXAMPLE_PLUGIN_REQUIREMENT(),
    source: 'plugin-bundled',
    cdn: EXAMPLE_PLUGIN_CDN(),
    name: 'Example Everything Plugin',
    description: 'Registers dummy implementations for every extension point'
};

const dependencies = {
    ...dependenciesJson,
    plugins: (dependenciesJson.plugins || []).map((dep) =>
        dep.id === pluginRequirement.id ? { ...dep, ...pluginRequirement } : dep
    )
};

// Resolve assets - will be resolved at runtime with CDN base URL if available
// Store raw asset paths for runtime resolution
const rawSpaceImage = spaceImage;
const rawPreviewImage = previewImage;

// Function to resolve asset URLs with optional CDN base URL
const resolveAssetUrl = (assetPath, cdnBaseUrl = null) => {
    if (!assetPath) return assetPath;
    
    // If CDN base URL is provided, use it (for plugins loaded from CDN)
    if (cdnBaseUrl) {
        try {
            const base = cdnBaseUrl.endsWith('/') ? cdnBaseUrl : `${cdnBaseUrl}/`;
            return new URL(assetPath, base).href;
        } catch (err) {
            // Fall through to other methods
        }
    }
    
    // Try using import.meta.url (works when plugin is loaded as ES module)
    try {
        const assetBase = new URL('.', import.meta.url).href;
        return new URL(assetPath, assetBase).href;
    } catch (err) {
        // Fall through to fallback
    }
    
    // Fallback: try using window location
    if (typeof window !== 'undefined') {
        const base = `${window.location.origin}/plugins/`;
        try {
            return new URL(assetPath, base).href;
        } catch {
            return `${base}${assetPath}`;
        }
    }
    
    return assetPath;
};

// Build board with resolved asset paths
const buildBoard = (cdnBaseUrl = null) => {
    const resolvedSpaceImage = resolveAssetUrl(rawSpaceImage, cdnBaseUrl);
    const resolvedPreviewImage = resolveAssetUrl(rawPreviewImage, cdnBaseUrl);
    
    return {
        $schema: 'https://boardgame.example.com/schemas/game-v3.json',
        version: EXAMPLE_PLUGIN_VERSION,
        type: 'game',
        metadata: {
            ...metadataJson,
            id: metadataJson.id || boardManifest.id,
            version: EXAMPLE_PLUGIN_VERSION,
            plugins: metadataJson.plugins || [
                { id: pluginRequirement.id, version: EXAMPLE_PLUGIN_REQUIREMENT(), source: 'plugin-bundled' }
            ],
            thumbnail: metadataJson.thumbnail || resolvedPreviewImage
        },
        requirements: {
            plugins: dependencies.plugins || [],
            minPlayers: dependencies.minPlayers,
            maxPlayers: dependencies.maxPlayers
        },
        engine: {
            type: engineJson.type,
            config: engineJson.config || {}
        },
        ui: {
            layout: uiJson.layout || 'standard-board',
            theme: uiJson.theme || {},
            components: uiJson.components || []
        },
        rules: {
            ...rulesJson,
            minPlayers: rulesJson.minPlayers ?? dependencies.minPlayers,
            maxPlayers: rulesJson.maxPlayers ?? dependencies.maxPlayers
        },
        board: {
            topology: {
                spaces: (topologyJson.spaces || []).map((space) => ({
                    ...space,
                    visual: {
                        ...space.visual,
                        image: resolvedSpaceImage
                    }
                })),
                connections: topologyJson.connections || []
            },
            rendering: {}
        }
    };
};

// Export the board builder function and a default board (for backward compatibility)
export const buildDemoBoard = buildBoard;
export const demoBoard = buildBoard();
